import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDbUser } from '../hooks/useDbUser';
import { db, MatterDeadline } from '../lib/db';
import { 
  DeadlineList,
  DeadlineCalendarWidget,
  DeadlineStats,
  DeadlineProgress,
  DeadlineTimeline
} from '../components/dashboard';
import { Calendar, List, BarChart, Clock } from 'lucide-react';

type ViewMode = 'list' | 'calendar' | 'timeline' | 'stats';

function DeadlinesPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  
  // Deadline data
  const [allDeadlines, setAllDeadlines] = useState<MatterDeadline[]>([]);
  const [deadlineStats, setDeadlineStats] = useState({
    overdue: 0,
    today: 0,
    thisWeek: 0,
    upcoming30Days: 0
  });
  
  // Filters
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadDeadlines();
    }
  }, [dbUserId, dbUserLoading]);

  useEffect(() => {
    // Handle navigation from dashboard with filter
    if (location.state?.filter) {
      setSelectedFilter(location.state.filter);
    }
  }, [location]);


  const loadDeadlines = async () => {
    if (!dbUserId) return;
    
    setLoading(true);
    try {
      const [upcoming, stats] = await Promise.all([
        db.deadlines.getUpcoming(dbUserId, 365), // Get all deadlines for the year
        db.deadlines.getStats(dbUserId)
      ]);
      
      setAllDeadlines(upcoming);
      setDeadlineStats(stats);
    } catch (error) {
      console.error('Error loading deadlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDeadlines = () => {
    let filtered = allDeadlines;
    
    // Apply filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (selectedFilter) {
      case 'overdue':
        filtered = filtered.filter(d => new Date(d.due_date) < today);
        break;
      case 'today':
        filtered = filtered.filter(d => {
          const deadlineDate = new Date(d.due_date);
          return deadlineDate.toDateString() === today.toDateString();
        });
        break;
      case 'thisWeek':
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        filtered = filtered.filter(d => {
          const deadlineDate = new Date(d.due_date);
          return deadlineDate >= today && deadlineDate <= weekEnd;
        });
        break;
      case 'upcoming30Days':
        const monthEnd = new Date(today);
        monthEnd.setDate(monthEnd.getDate() + 30);
        filtered = filtered.filter(d => {
          const deadlineDate = new Date(d.due_date);
          return deadlineDate >= today && deadlineDate <= monthEnd;
        });
        break;
    }
    
    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(d => 
        d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  const handleDeadlineClick = (deadline: MatterDeadline) => {
    switch (deadline.deadline_type) {
      case 'task':
        // Navigate to the dashboard with the goal containing this task
        // Note: matter_id contains the goal_id for task deadlines
        if (deadline.matter_id) {
          navigate('/dashboard', { state: { goalId: deadline.matter_id, taskId: deadline.id, highlightTask: true } });
        } else {
          // If no goal, just go to dashboard
          navigate('/dashboard');
        }
        break;
      case 'deliverable':
        // Navigate to the dashboard/deliverables page
        navigate('/dashboard', { state: { goalId: deadline.id, highlightGoal: true } });
        break;
      case 'project':
        // Navigate to the projects page
        navigate('/projects', { state: { projectId: deadline.id, highlightProject: true } });
        break;
      case 'matter':
        // Navigate to the matters page
        navigate('/matters', { state: { matterId: deadline.id, highlightMatter: true } });
        break;
      case 'matter_deadline':
        // Navigate to the matters page with the specific matter highlighted
        if (deadline.matter_id) {
          navigate('/matters', { state: { matterId: deadline.matter_id, highlightDeadline: deadline.id } });
        }
        break;
      default:
        // Fallback to matters if matter_id exists
        if (deadline.matter_id) {
          navigate('/matters', { state: { matterId: deadline.matter_id, highlightDeadline: deadline.id } });
        }
    }
  };

  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading deadlines...</div>
      </div>
    );
  }

  const filteredDeadlines = getFilteredDeadlines();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Deadlines</h1>
        
        {/* View Mode Selector */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md flex items-center space-x-2 ${
                viewMode === 'list' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="w-4 h-4" />
              <span>List</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-2 rounded-md flex items-center space-x-2 ${
                viewMode === 'calendar' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>Calendar</span>
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-2 rounded-md flex items-center space-x-2 ${
                viewMode === 'timeline' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>Timeline</span>
            </button>
            <button
              onClick={() => setViewMode('stats')}
              className={`px-3 py-2 rounded-md flex items-center space-x-2 ${
                viewMode === 'stats' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BarChart className="w-4 h-4" />
              <span>Statistics</span>
            </button>
          </div>
          
          {/* Search */}
          <input
            type="text"
            placeholder="Search deadlines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        
        {/* Stats Summary */}
        <DeadlineProgress stats={deadlineStats} />
      </div>
      
      {/* View Content */}
      <div className="bg-white rounded-lg shadow">
        {viewMode === 'list' && (
          <DeadlineList 
            deadlines={filteredDeadlines}
            onDeadlineClick={handleDeadlineClick}
            loading={loading}
          />
        )}
        
        {viewMode === 'calendar' && (
          <div className="p-6">
            <DeadlineCalendarWidget 
              deadlines={filteredDeadlines}
              onDeadlineClick={handleDeadlineClick}
            />
          </div>
        )}
        
        {viewMode === 'timeline' && (
          <div className="p-6">
            <DeadlineTimeline 
              deadlines={filteredDeadlines}
              onDeadlineClick={handleDeadlineClick}
            />
          </div>
        )}
        
        {viewMode === 'stats' && (
          <div className="p-6">
            <DeadlineStats 
              deadlines={allDeadlines}
              stats={deadlineStats}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DeadlinesPage;